import * as React from 'react';
import * as uuid from 'uuid/v4';
import {
    Dialog,
    DialogTitle,
    DialogActions,
    DialogContent,
    DialogContentText,
    Divider,
    TextField,
    IconButton,
    Button,
    Box,
    Grid,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core';
import { assoc, pipe } from 'ramda';
import { useEffect, useState } from 'react';
import { compose } from 'fp-ts/lib/function';
import { withStyles } from '@material-ui/core/styles';
import { telestrationBlob } from '../Utils/Recording';
import { setLastTelestratedVideo, withTelestrationState } from '../State';
import { ITelestrationStateMgr } from '../Types';
import {
    telestrationSavingStarted,
    telestrationRecordingSavingCanceled,
    telestrationRecordingSavingFinished,
} from 'src/App/UserEvents';
import { sendUserEvent } from 'src/App/UserEvents/UserEventManager';
import { ILocalStateMgr, withLocalState } from 'src/App/LocalState';
import { beamVideo, IUploadVideo } from 'src/Utilities/AwsVideoLibrary';
import SaveIcon from '../../../Assets/save-icon.png';
import { CloseIcon } from 'src/Assets/svgs/CloseIcon';

const styles = makeStyles((theme) => ({
    paper: {
        borderRadius: '5px',
        width: '500px',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    telestrationTitle: {
        width: '100%',
        marginBottom: 8,
        '& .MuiOutlinedInput-root': {
            borderWidth: 1,
            borderRadius: 8,
            fontSize: 14,
            '& input': {
                padding: '16px 8px',
            },
            '&.MuiOutlinedInput-multiline': {
                padding: 0,
                '& textarea': {
                    padding: '16px 8px',
                },
            },
            '&:hover fieldset': {
                borderWidth: 1,
                borderColor: '#ffffff',
            },
            '& fieldset': {
                borderWidth: 1,
                transition: 'border-width 0.3s ease',
            },
            '&.Mui-focused fieldset': {
                borderWidth: 1,
                borderColor: '#ffffff',
            },
        },
        '&:last-child': {
            marginBottom: 0,
        },
    },
    btnsContainer: {
        display: 'block',
        padding: '0px',
    },
    btn: {
        color: theme.palette.common.white,
        marginBottom: '10px',
        marginRight: '10px',
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.spacing(1),
        width: '28%',
        fontSize: '14px',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    progressDiv: {
        margin: '110px 30px 0',
        textAlign: 'center' as 'center',
    },
    cardHeaderCss: {
        cursor: 'pointer',
        borderBottom: '1px solid #282833',
    },
    actionFooterCss: {
        borderTop: '1px solid #3F3F50',
        padding: '16px',
        textAlign: 'right' as 'right',
        gap: 8,
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: '0 !important',
        marginTop: 16,
        '& button': {
            borderRadius: 8,
            padding: '0 16px',
            height: 40,
            fontWeight: 400,
            textTransform: 'capitalize',
            '&.MuiButton-contained:hover': {
                backgroundColor: '#B3211E',
            },
        },
    },
    dialogBox: {
        backgroundColor: '#121217',
        zIndex: 999999,
        margin: 0,
        padding: 0,
        border: '1px solid #3F3F50',
        borderRadius: '8px',
    },
    dialogHeader: {
        padding: '12px 16px',
        borderBottom: '1px solid #3F3F50',
        '& .close-icon': {
            cursor: 'pointer',
            width: 38,
            height: 38,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            '&:hover': {
                backgroundColor: 'rgba(250, 250, 250, 0.1)',
            },
        },
    },
    dialogTitle: {
        '&&': {
            padding: 0,
            '& h2': {
                fontSize: 16,
            },
        },
    },
}));

interface ISaveTelestrationModalProps {
    telestratedvideoID: string;
    localStateMgr: ILocalStateMgr;
    telestrationStateMgr: ITelestrationStateMgr;
    open: boolean;
    closeModal: any;
    onSaveTelestration: any;
    onProgress: any;
    onSuccess: any;
    onStartConversion: any;
    onStartDownload: any;
}

const saveTelestrationModal = ({
    telestratedvideoID,
    localStateMgr,
    telestrationStateMgr,
    open,
    closeModal,
    onSaveTelestration,
    onProgress,
    onSuccess,
    onStartConversion,
    onStartDownload,
}: ISaveTelestrationModalProps) => {
    const initFormState = {
        title: '',
        description: '',
    };
    const [formState, updateFormState] = useState(initFormState);
    const { dispatchAction } = telestrationStateMgr;
    const onFieldChange = (fieldName: string) => ({
        currentTarget: { value },
    }: React.ChangeEvent<HTMLInputElement>) => {
        updateFormState({
            ...formState,
            [fieldName]: value,
        });
    };

    const clearState = () => updateFormState(initFormState);

    useEffect(() => {
        const personalToken = localStorage.getItem('authToken');
        if (!personalToken) {
            const params: any = new Proxy(
                new URLSearchParams(window.location.search),
                {
                    get: (searchParams, prop: any) => searchParams.get(prop),
                }
            );
            const authToken: any = params.authToken;
            localStorage.setItem('authToken', authToken);
        }
    }, []);

    const onSaveClick = () => {
        const id = uuid();
        sendUserEvent(telestrationSavingStarted, window.location.href, id);
        const { blob }: { blob: Blob } = telestrationBlob();
        const file = new File([blob], formState.title);

        const _onUploadSuccess = () => onSuccess(id);
        const _onUploadProgress = (percent: number) => onProgress(id, percent);
        const _onUploadStartConversation = () => onStartConversion(id);
        const _onUploadStartDownload = () => onStartDownload(id);

        onSaveTelestration(id, formState.title);
        const videoPayload = pipe(
            assoc('type', 'telestration'),
            assoc('id', id),
            assoc('title', formState.title),
            assoc('subtitle', formState.description),
            assoc('videoFile', file),
            assoc('videoType', 'telestration'),
            assoc('onSuccess', _onUploadSuccess),
            assoc('onProgress', _onUploadProgress),
            assoc('onStartConversion', _onUploadStartConversation),
            assoc('onStartDownload', _onUploadStartDownload)
        )({
            cancelTokenSource: localStateMgr.state.VideoUploadingCancelToken,
            saveTelestratedVideoLink: (value: string) => {
                console.log('value', value);
                const url = process.env.REACT_APP_SHARE_URI ?? '';
                dispatchAction(
                    setLastTelestratedVideo(
                        url + '/v/' + value.split('/').slice(0, -1).pop() ?? ''
                    )
                );
            },
        }) as IUploadVideo;
        beamVideo(videoPayload).then(() =>
            sendUserEvent(
                telestrationRecordingSavingFinished,
                window.location.href,
                id
            )
        );
        closeModal();
        clearState();
    };

    const classes = styles();

    return (
        <Dialog open={open} classes={{ paper: classes.paper }}>
            <Box className={classes.dialogBox} style={{ maxWidth: '100%' }}>
                <Grid
                    container
                    spacing={0}
                    alignItems='center'
                    className={classes.dialogHeader}
                >
                    <Grid item xs={9}>
                        <Box display='flex' alignItems='center'>
                            <img
                                src={SaveIcon}
                                alt='edit video'
                                width='16'
                                height='16'
                                style={{ marginRight: 12 }}
                            />
                            <DialogTitle className={classes.dialogTitle}>
                                {'Save Recording'}
                            </DialogTitle>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box display='flex' justifyContent='flex-end'>
                            <span
                                // aria-label='Close modal'
                                className={'close-icon'}
                                onClick={() => closeModal()}
                            >
                                <CloseIcon />
                            </span>
                        </Box>
                    </Grid>
                </Grid>
                <DialogContent
                    style={{
                        padding: '16px 16px 0',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <label
                        style={{
                            display: 'block',
                            marginBottom: 8,
                            fontWeight: 500,
                        }}
                    >
                        Clip Name
                        <span style={{ color: '#DB2C29' }}>{' \u002A'}</span>
                    </label>
                    <TextField
                        className={classes.telestrationTitle}
                        autoComplete='off'
                        required
                        fullWidth
                        // label='Title'
                        placeholder='Enter name'
                        id='video-title'
                        variant='outlined'
                        value={formState.title}
                        onChange={onFieldChange('title' as 'title')}
                    />
                    <label
                        style={{
                            display: 'block',
                            marginBottom: 8,
                            fontWeight: 500,
                        }}
                    >
                        Notes
                    </label>
                    <TextField
                        className={classes.telestrationTitle}
                        autoComplete='off'
                        multiline
                        rows='4'
                        placeholder=''
                        fullWidth
                        // label='Notes'
                        value={formState.description}
                        onChange={onFieldChange('description' as 'description')}
                        id='video-notes'
                        variant='outlined'
                    />
                </DialogContent>

                <DialogActions className={classes.btnsContainer}>
                    <div className={classes.actionFooterCss}>
                        <Button
                            variant='outlined'
                            onClick={() => {
                                closeModal();
                                clearState();
                                sendUserEvent(
                                    telestrationRecordingSavingCanceled,
                                    window.location.href,
                                    telestratedvideoID
                                );
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={formState.title.length < 2}
                            variant='contained'
                            color='primary'
                            onClick={onSaveClick}
                        >
                            Save
                        </Button>
                    </div>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export const SaveTelestrationModal = compose(
    withTelestrationState,
    withLocalState
)(saveTelestrationModal);
